import API_CONFIG from '../config/api';
import { fetchAuthSession } from 'aws-amplify/auth';

/**
 * Get the current user's authentication token
 */
async function getAuthToken(): Promise<string | undefined> {
  try {
    const session = await fetchAuthSession();
    return session.tokens?.idToken?.toString();
  } catch (error) {
    console.error('Failed to get auth token:', error);
    return undefined;
  }
}

/**
 * Response shape for GET /v1/fee-position/latest. Field set is pinned by the backend's
 * FeePositionApiHandlerStructureTest — keep in sync.
 */
export interface FeePositionResponse {
  pricingTier: string;
  makerFeeRate: string;
  takerFeeRate: string;
  qualificationType: string | null;
  qualifyingValue: string | null;
  volumeToNextTier: string | null;
  headroomAboveCurrentTier: string | null;
  projectedTierLossDate: string | null;
  hasPromoFee: boolean | null;
  hasCostPlusCommission: boolean | null;
  totalBalance: string | null;
  capturedAt: string;
  ageSeconds: number;
}

/**
 * One entry of GET /v1/fee-position/history.
 */
export interface FeePositionHistoryEntry {
  capturedAt: string;
  pricingTier: string;
  qualifyingValue: string | null;
  tierTransition: boolean;
}

/**
 * Fetch the current fee position — mirrors getLatestPortfolioBalance's inline fetch shape (this module
 * uses its own dedicated API Gateway, not the shared apiFetch helper's single baseUrl).
 */
export async function getFeePositionLatest(): Promise<FeePositionResponse> {
  const url = `${API_CONFIG.feePositionBaseUrl}/v1/fee-position/latest`;

  const authToken = await getAuthToken();
  if (!authToken) {
    throw new Error('Authentication required. Please log in.');
  }

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), API_CONFIG.timeout);

  try {
    const response = await fetch(url, {
      signal: controller.signal,
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${authToken}`,
      },
    });

    clearTimeout(timeoutId);

    if (!response.ok) {
      if (response.status === 401) {
        throw new Error('Authentication failed. Please log in again.');
      }
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    clearTimeout(timeoutId);
    if (error instanceof Error) {
      if (error.name === 'AbortError') {
        throw new Error('Request timeout - please try again');
      }
      throw error;
    }
    throw new Error('An unexpected error occurred');
  }
}

/**
 * Fetch fee position history over a trailing window.
 * @param hours - Number of hours of history to fetch
 */
export async function getFeePositionHistory(hours: number): Promise<FeePositionHistoryEntry[]> {
  const url = `${API_CONFIG.feePositionBaseUrl}/v1/fee-position/history?hours=${hours}`;

  const authToken = await getAuthToken();
  if (!authToken) {
    throw new Error('Authentication required. Please log in.');
  }

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), API_CONFIG.timeout);

  try {
    const response = await fetch(url, {
      signal: controller.signal,
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${authToken}`,
      },
    });

    clearTimeout(timeoutId);

    if (!response.ok) {
      if (response.status === 401) {
        throw new Error('Authentication failed. Please log in again.');
      }
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    clearTimeout(timeoutId);
    if (error instanceof Error) {
      if (error.name === 'AbortError') {
        throw new Error('Request timeout - please try again');
      }
      throw error;
    }
    throw new Error('An unexpected error occurred');
  }
}
